"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { ScrollReveal } from "@/ui/components/marketing/scroll-reveal";
import { publicStorePath } from "@/lib/storefront/public-url";
import { useLocale } from "next-intl";

interface AffiliateProgrammeProps {
	formIdSuffix?: string;
	withTopBorder?: boolean;
}

export function AffiliateProgramme({ formIdSuffix = "", withTopBorder = false }: AffiliateProgrammeProps) {
	const t = useTranslations("partners.affiliateProgramme");
	const locale = useLocale();
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const benefits = t.raw("benefits") as string[];
	const idEmail = `aff-email${formIdSuffix ? `-${formIdSuffix}` : ""}`;
	const idChannel = `aff-channel${formIdSuffix ? `-${formIdSuffix}` : ""}`;

	async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSubmitting(true);
		setError(null);
		const form = e.currentTarget;
		const fd = new FormData(form);
		const email = String(fd.get("email") ?? "").trim();
		const channel = String(fd.get("channel") ?? "").trim();
		const message = channel ? `Channel: ${channel}` : "Affiliate programme signup via partners page";

		try {
			const res = await fetch("/api/affiliate/apply", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					partnerType: "INFLUENCER",
					name: email.split("@")[0] || "Affiliate",
					email,
					message,
				}),
			});
			if (!res.ok) {
				const data = (await res.json().catch(() => ({}))) as { error?: string };
				throw new Error(data.error ?? t("errorGeneric"));
			}
			setDone(true);
			form.reset();
		} catch (err) {
			setError(err instanceof Error ? err.message : t("errorGeneric"));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<section
			className={`bg-background py-10 sm:py-14 lg:py-20 ${withTopBorder ? "border-t border-border" : ""}`}
		>
			<div className="mx-auto max-w-[1500px] px-3 sm:px-6 lg:px-8">
				<ScrollReveal>
					<div
						className="relative overflow-hidden rounded-xl border border-white/15 text-white shadow-[0_30px_80px_-40px_rgba(0,0,0,0.65)] sm:rounded-2xl lg:rounded-3xl"
						style={{
							backgroundColor: "#04132a",
							backgroundImage: "linear-gradient(135deg, #04132a 0%, #082445 50%, #03101f 100%)",
						}}
					>
						{/* Deepening black overlay — matches science hero's layered depth */}
						<div
							aria-hidden
							className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/45"
						/>
						<div
							aria-hidden
							className="absolute inset-0 opacity-[0.08]"
							style={{
								backgroundImage:
									"linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
								backgroundSize: "44px 44px",
							}}
						/>
						{/* Corner crosshairs */}
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="pointer-events-none absolute left-3 top-3 h-3 w-3 text-white/30"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="pointer-events-none absolute right-3 top-3 h-3 w-3 rotate-90 text-white/30"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="pointer-events-none absolute bottom-3 left-3 h-3 w-3 -rotate-90 text-white/30"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="pointer-events-none absolute bottom-3 right-3 h-3 w-3 rotate-180 text-white/30"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>
						<div className="relative grid items-start gap-6 px-4 py-8 sm:gap-10 sm:px-10 sm:py-14 lg:grid-cols-[1.3fr_1fr] lg:items-center lg:gap-14 lg:px-14 lg:py-16">
							<div>
								<div className="text-primary-foreground/70 flex flex-wrap items-center gap-x-3 gap-y-1.5 font-mono text-[0.56rem] uppercase tracking-[0.18em] sm:gap-x-5 sm:text-[0.62rem] sm:tracking-[0.22em]">
									<span className="flex items-center gap-2 text-primary-foreground">
										<span className="bg-primary-foreground/60 block h-px w-6 sm:w-8" />
										{t("eyebrow")}
									</span>
									<span className="text-primary-foreground/30 hidden sm:inline">|</span>
									<span>{t("tag")}</span>
								</div>
								<h2 className="mt-4 text-[clamp(1.55rem,4vw,3rem)] font-medium leading-[1.1] tracking-[-0.018em] sm:mt-6 sm:leading-[1.05] sm:tracking-[-0.022em]">
									{t("title")}
								</h2>
								<p className="text-primary-foreground/80 mt-3 max-w-xl text-[0.88rem] leading-[1.6] sm:mt-5 sm:text-[0.95rem] sm:leading-[1.7] lg:text-base">
									{t("intro")}
								</p>
								<ul className="mt-5 grid max-w-xl gap-x-4 gap-y-1.5 sm:mt-7 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-2.5">
									{benefits.map((b) => (
										<li
											key={b}
											className="text-primary-foreground/85 flex items-start gap-2 font-mono text-[0.58rem] uppercase leading-snug tracking-[0.06em] sm:text-[0.68rem] sm:tracking-[0.08em]"
										>
											<svg
												viewBox="0 0 12 12"
												className="text-primary-foreground/90 mt-0.5 h-2.5 w-2.5 shrink-0 sm:h-3 sm:w-3"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.8"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden
											>
												<path d="M2 6 L5 9 L10 3" />
											</svg>
											{b}
										</li>
									))}
								</ul>
							</div>

							<div className="border-primary-foreground/20 bg-primary-foreground/[0.04] relative overflow-hidden rounded-xl border backdrop-blur-sm sm:rounded-2xl">
								<div className="border-primary-foreground/10 flex items-center justify-between border-b px-4 pb-2.5 pt-3.5 sm:px-6 sm:pb-3 sm:pt-5">
									<div className="flex items-center gap-2 font-mono text-[0.54rem] uppercase tracking-[0.18em] text-primary-foreground sm:text-[0.58rem] sm:tracking-[0.22em]">
										<span className="relative flex h-1.5 w-1.5">
											<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
										</span>
										{t("formLive")}
									</div>
									<span className="text-primary-foreground/55 font-mono text-[0.5rem] uppercase tracking-[0.16em] sm:text-[0.55rem] sm:tracking-[0.18em]">
										{t("formFree")}
									</span>
								</div>

								{done ? (
									<div className="space-y-3 px-4 py-6 sm:px-6 sm:py-8">
										<p className="font-medium text-primary-foreground">{t("successTitle")}</p>
										<p className="text-primary-foreground/80 text-sm">{t("successBody")}</p>
										<Link
											href={publicStorePath(locale, "/partners/apply?type=influencer")}
											className="text-primary-foreground/90 text-sm underline"
										>
											{t("fullApplicationLink")}
										</Link>
									</div>
								) : (
									<form onSubmit={onSubmit} className="space-y-3 px-4 py-4 sm:space-y-4 sm:px-6 sm:py-6">
										<div>
											<label
												htmlFor={idEmail}
												className="text-primary-foreground/70 mb-1.5 block font-mono text-[0.52rem] uppercase tracking-[0.18em] sm:mb-2 sm:text-[0.56rem] sm:tracking-[0.2em]"
											>
												{t("emailLabel")}
											</label>
											<input
												id={idEmail}
												name="email"
												type="email"
												required
												placeholder={t("emailPlaceholder")}
												className="focus:ring-primary-foreground/20 border-primary-foreground/20 bg-primary-foreground/5 placeholder:text-primary-foreground/40 focus:border-primary-foreground/40 focus:bg-primary-foreground/10 w-full rounded-lg border px-3.5 py-2.5 text-[0.86rem] text-primary-foreground transition-colors focus:outline-none focus:ring-[3px] sm:px-4 sm:py-3 sm:text-[0.92rem]"
											/>
										</div>
										<div>
											<label
												htmlFor={idChannel}
												className="text-primary-foreground/70 mb-1.5 block font-mono text-[0.52rem] uppercase tracking-[0.18em] sm:mb-2 sm:text-[0.56rem] sm:tracking-[0.2em]"
											>
												{t("channelLabel")}
											</label>
											<input
												id={idChannel}
												name="channel"
												type="text"
												placeholder={t("channelPlaceholder")}
												className="focus:ring-primary-foreground/20 border-primary-foreground/20 bg-primary-foreground/5 placeholder:text-primary-foreground/40 focus:border-primary-foreground/40 focus:bg-primary-foreground/10 w-full rounded-lg border px-3.5 py-2.5 text-[0.86rem] text-primary-foreground transition-colors focus:outline-none focus:ring-[3px] sm:px-4 sm:py-3 sm:text-[0.92rem]"
											/>
										</div>
										<label className="flex cursor-pointer items-start gap-2 pt-0.5 sm:gap-2.5 sm:pt-1">
											<input
												type="checkbox"
												required
												name="consent"
												className="mt-0.5 h-3 w-3 shrink-0 accent-primary-foreground sm:mt-1 sm:h-3.5 sm:w-3.5"
											/>
											<span className="text-primary-foreground/65 font-mono text-[0.54rem] uppercase leading-snug tracking-[0.12em] sm:text-[0.58rem] sm:leading-relaxed sm:tracking-[0.14em]">
												{t("consent")}
											</span>
										</label>
										{error && <p className="text-sm text-destructive">{error}</p>}
										<button
											type="submit"
											disabled={submitting}
											className="hover:bg-primary-foreground/90 group inline-flex w-full items-center justify-between gap-2 rounded-full bg-primary-foreground px-4 py-3 text-[0.64rem] font-medium uppercase tracking-[0.16em] text-primary transition-colors disabled:opacity-60 sm:px-5 sm:py-3.5 sm:text-[0.7rem] sm:tracking-[0.18em]"
										>
											<span>{submitting ? t("submitting") : t("submit")}</span>
											<span aria-hidden className="group-hover:translate-x-1 transition-transform">
												→
											</span>
										</button>
										<p className="text-primary-foreground/45 pt-0.5 font-mono text-[0.48rem] uppercase leading-snug tracking-[0.12em] sm:pt-1 sm:text-[0.52rem] sm:leading-relaxed sm:tracking-[0.16em]">
											{t("footnote")}
										</p>
									</form>
								)}
							</div>
						</div>
					</div>
				</ScrollReveal>
			</div>
		</section>
	);
}
